import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Chip,
  Paper,
  TablePagination,
  TextField,
  Typography,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import {
  actions,
  loadSuggestionOverrides,
  publishSuggestionOverrides,
  selectHasChanges,
  selectHasInvalidSuggestionOverrides,
} from 'slices/SuggestionOverridesSlice';
import SuggestionOverrideRuleCard from './SuggestionOverrideRuleCard';
import styles from './SuggestionOverrides.module.scss';

const SuggestionOverridesPage = () => {
  const dispatch = useDispatch();
  const t = useLocalizedMessage();
  const { currentShop } = useSelector(state => state.app);
  const {
    rules, revision, isFetching, isSaving, loadError,
  } = useSelector(state => state.suggestionOverrides);
  const hasChanges = useSelector(selectHasChanges);
  const hasInvalid = useSelector(selectHasInvalidSuggestionOverrides);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (currentShop) dispatch(loadSuggestionOverrides(currentShop));
  }, [currentShop, dispatch]);

  useEffect(() => setPage(0), [search, rowsPerPage]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return rules;
    return rules.filter(rule => (
      rule.query.toLowerCase().includes(needle)
      || rule.language_id.toLowerCase().includes(needle)
      || rule.terms.values.join(' ').toLowerCase().includes(needle)
      || rule.words.values.join(' ').toLowerCase().includes(needle)
    ));
  }, [rules, search]);
  const visible = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  if (!currentShop) return null;
  return (
    <SearchFeaturePageShell
      pageId="customizationSuggestionOverrides"
      description={t('suggestionOverrides.description')}
      isLoading={isFetching}
      error={loadError && !isFetching ? loadError : null}
      actions={(
        <SaveActionBtnGrp
          isFetching={isFetching || isSaving}
          buttons={[
            {
              text: t('suggestionOverrides.actions.publish'),
              action: () => dispatch(publishSuggestionOverrides(currentShop)),
              color: 'primary',
              disabled: !hasChanges || hasInvalid,
              testId: 'suggestion-overrides-publish',
            },
            {
              text: t('common.discard'),
              action: () => dispatch(actions.discardChanges()),
              color: 'secondary',
              disabled: !hasChanges,
              testId: 'suggestion-overrides-discard',
            },
          ]}
        />
      )}
    >
      <section className={styles.page} data-testid="suggestion-overrides-page">
        <Paper variant="outlined" className={styles.toolbar}>
          <div>
            <Typography variant="h6">{t('suggestionOverrides.rules.title')}</Typography>
            <Typography variant="body2" color="textSecondary">
              {t('suggestionOverrides.rules.hint')}
            </Typography>
          </div>
          <div className={styles.toolbarActions}>
            <Chip
              size="small"
              label={`${t('suggestionOverrides.revision')} ${revision}`}
              data-testid="suggestion-overrides-revision"
            />
            <TextField
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder={t('suggestionOverrides.search')}
              variant="outlined"
              size="small"
              inputProps={{ 'aria-label': t('suggestionOverrides.search') }}
            />
            <Button
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => dispatch(actions.addRule())}
              data-testid="suggestion-overrides-add"
            >
              {t('suggestionOverrides.actions.add')}
            </Button>
          </div>
        </Paper>

        {hasInvalid && (
          <div className={styles.validation} role="status">
            {t('suggestionOverrides.validation.fixRules')}
          </div>
        )}

        <div className={styles.ruleList}>
          {visible.map(rule => (
            <SuggestionOverrideRuleCard
              key={rule._id}
              rule={rule}
              onUpdate={(id, changes) => dispatch(actions.updateRule({ id, changes }))}
              onDelete={id => dispatch(actions.deleteRule(id))}
            />
          ))}
          {!visible.length && (
            <Paper variant="outlined" className={styles.emptyState}>
              <Typography color="textSecondary">{t('suggestionOverrides.empty')}</Typography>
            </Paper>
          )}
        </div>

        <TablePagination
          component="div"
          count={filtered.length}
          page={Math.min(page, Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1))}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(event, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
        />
      </section>
    </SearchFeaturePageShell>
  );
};

export default SuggestionOverridesPage;
